import { useContext, useState } from 'react';
import DonutChart from '../DonutChart';
import useI18n from '@/hooks/useI18n';
import { formatCount } from '@/utils/numberUtils';
import { UserContext } from '@/context/UserContext';
import {
  useContentAnalysisChartQuery,
  useEngagementAnalysisChartQuery,
} from '@/hooks/queries/useAnalyticsQueries';
import LegendDot from './LegendDot';
import {
  CONTENT_PIE_COLORS,
  ENGAGEMENT_COLORS,
  TEXT_DEFAULT,
  TEXT_HEADING,
  TEXT_MUTED,
  BORDER_DEFAULT,
} from './analyticsTokens';

// ApexCharts' stacked horizontal bars can't round only the outer edges of
// the stack (every native combo leaves either the left edge flat or the last
// segment as a disconnected pill). Rendering as flex+overflow-hidden gives
// the rounded-outer / flat-internal-joins look the design calls for.

const ContentAnalysisComponent = ({
  chartFilter,
  customStartDate,
  customEndDate,
}: any) => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;

  const brandId = user?.brand?._id || '';
  const isRealUser = user?.role === 'brandUser' && !isDummyUser && !!brandId;

  const { data: contentData } = useContentAnalysisChartQuery(
    brandId,
    chartFilter,
    isRealUser,
    customStartDate,
    customEndDate,
  );
  const { data: engagementData, isLoading: isEngagementLoading } =
    useEngagementAnalysisChartQuery(
      brandId,
      chartFilter,
      isRealUser,
      customStartDate,
      customEndDate,
    );

  // Demo content split (92 submissions: 18 IG Posts, 34 IG Reels + 28 TikTok
  // videos folded into the video/reel bucket, 12 IG Stories):
  //   post 18/92 ≈ 20%   reel(video) 62/92 ≈ 67%   story 12/92 ≈ 13%
  const postPercentage = isDummyUser ? 20 : contentData?.percentage?.posts || 0;
  const reelPercentage = isDummyUser ? 67 : contentData?.percentage?.reels || 0;
  const storyPercentage = isDummyUser
    ? 13
    : contentData?.percentage?.stories || 0;

  // [post, reel, story] demo engagement totals — views 327,000 / likes 18,300.
  // Comment counts are scaled up to ~75% of the per-row like count so the
  // comment segment is actually visible in the Most Engaging Content Form bars
  // (at realistic counts comments are <1% of views and render sub-pixel).
  // Views and likes are unchanged, so Performance Overview, the engagement rate
  // and the content-split donut all stay the same.
  // `video` carries the brand's TikTok totals. It folds into the reel row for
  // the same reason the donut counts TikTok videos as reels — otherwise a
  // TikTok-only brand shows "No Data" here while its donut renders normally.
  const engagementViews = isDummyUser
    ? [64000, 221000, 42000]
    : [
        engagementData?.post?.totalViews ?? 0,
        (engagementData?.reel?.totalViews ?? 0) +
          (engagementData?.video?.totalViews ?? 0),
        engagementData?.story?.totalViews ?? 0,
      ];
  const engagementLikes = isDummyUser
    ? [3600, 12300, 2400]
    : [
        engagementData?.post?.totalLikes ?? 0,
        (engagementData?.reel?.totalLikes ?? 0) +
          (engagementData?.video?.totalLikes ?? 0),
        engagementData?.story?.totalLikes ?? 0,
      ];
  const engagementComments = isDummyUser
    ? [2600, 9000, 1700]
    : [
        engagementData?.post?.totalComments ?? 0,
        (engagementData?.reel?.totalComments ?? 0) +
          (engagementData?.video?.totalComments ?? 0),
        engagementData?.story?.totalComments ?? 0,
      ];

  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      <h2
        style={{
          fontFamily: 'Manrope',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '120%',
          opacity: 0.9,
          color: TEXT_HEADING,
        }}
      >
        {t('common.contentAnalysis')}
      </h2>
      <div
        className="rounded-[24px] flex bg-white"
        style={{
          padding: 24,
          gap: 32,
          border: `1px solid ${BORDER_DEFAULT}`,
        }}
      >
        {/* Left panel — Most Participated Content form (358px hug) */}
        <div className="flex flex-col" style={{ width: 358, gap: 16 }}>
          {postPercentage + reelPercentage + storyPercentage === 0 ? (
            <div className="flex justify-center items-center h-[200px]">
              <span className="text-xl font-medium text-[#9B919D]">
                {String(t('common.noData'))}
              </span>
            </div>
          ) : (
            <DonutChart
              series={[postPercentage, reelPercentage, storyPercentage]}
              labels={[t('common.post'), t('common.reel'), t('common.story')]}
              colors={[
                CONTENT_PIE_COLORS.post,
                CONTENT_PIE_COLORS.reel,
                CONTENT_PIE_COLORS.story,
              ]}
              title={t('common.mostParticipatedContentForm')}
              height={220}
            />
          )}
        </div>

        {/* Vertical divider between the two panels — 88px tall, centered
            (Figma "Devider": #E5E7E8, not a full-height border). */}
        <div
          className="self-center"
          style={{
            width: 1,
            height: 88,
            background: '#E5E7E8',
            flexShrink: 0,
          }}
        />

        {/* Right panel — Most Engaging Content Form (40px L/R padding).
            Fills the remaining card width so the engagement bars stretch the
            full panel like the Figma, instead of being capped to the left. */}
        <div
          className="flex-1 flex flex-col"
          style={{
            paddingLeft: 40,
            paddingRight: 40,
            gap: 16,
          }}
        >
          {isEngagementLoading ? (
            <div className="flex justify-center items-center h-[200px]">
              <span className="text-xl font-medium text-[#9B919D]">
                {String(t('common.loading'))}
              </span>
            </div>
          ) : engagementViews.every((v) => v === 0) &&
            engagementLikes.every((v) => v === 0) &&
            engagementComments.every((v) => v === 0) ? (
            <div className="flex justify-center items-center h-[200px]">
              <span className="text-xl font-medium text-[#9B919D]">
                {String(t('common.noData'))}
              </span>
            </div>
          ) : (
            <>
              <div
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: '16px',
                  color: TEXT_MUTED,
                }}
              >
                {t('common.mostEngagingContentForm')}
              </div>
              <div
                className="flex flex-col"
                style={{ paddingTop: 20, paddingBottom: 20, gap: 24 }}
              >
                <div className="flex flex-col" style={{ gap: 12 }}>
                  {(() => {
                    // Slot sizes are determined by the power-transformed global
                    // max of each metric so the highest-count metric (views)
                    // gets more track real estate than likes/comments, preserving
                    // internal bar proportions. Within each slot, values scale
                    // linearly against that metric's own max so equal counts
                    // across rows always render as equal segment widths.
                    const maxViews = Math.max(...engagementViews, 1);
                    const maxLikes = Math.max(...engagementLikes, 1);
                    const maxComments = Math.max(...engagementComments, 1);
                    const pw = (v: number) => Math.pow(Math.max(v, 0), 0.4);
                    const pwTotal =
                      pw(maxViews) + pw(maxLikes) + pw(maxComments);
                    const viewSlot = (pw(maxViews) / pwTotal) * 100;
                    const likeSlot = (pw(maxLikes) / pwTotal) * 100;
                    const commentSlot = (pw(maxComments) / pwTotal) * 100;
                    return [
                      t('common.post'),
                      t('common.reel'),
                      t('common.story'),
                    ].map((label, i) => (
                      <EngagementBarRow
                        key={label}
                        label={label}
                        views={engagementViews[i]}
                        likes={engagementLikes[i]}
                        comments={engagementComments[i]}
                        viewTrackPct={
                          (engagementViews[i] / maxViews) * viewSlot
                        }
                        likeTrackPct={
                          (engagementLikes[i] / maxLikes) * likeSlot
                        }
                        commentTrackPct={
                          (engagementComments[i] / maxComments) * commentSlot
                        }
                      />
                    ));
                  })()}
                </div>
                <div className="flex" style={{ gap: 16 }}>
                  <LegendDot
                    color={ENGAGEMENT_COLORS.view}
                    label={t('userManagement.actions.view')}
                    gap={8}
                  />
                  <LegendDot
                    color={ENGAGEMENT_COLORS.like}
                    label={t('common.like')}
                    gap={8}
                  />
                  <LegendDot
                    color={ENGAGEMENT_COLORS.comments}
                    label={String(t('common.comment'))}
                    gap={8}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EngagementBarRow = ({
  label,
  views,
  likes,
  comments,
  viewTrackPct,
  likeTrackPct,
  commentTrackPct,
}: {
  label: string;
  views: number;
  likes: number;
  comments: number;
  /** Each metric's width as a % of the full track, globally normalized so
   *  equal raw counts always produce equal visual segment widths across rows. */
  viewTrackPct: number;
  likeTrackPct: number;
  commentTrackPct: number;
}) => {
  const hasEngagement = views + likes + comments > 0;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex items-center relative"
      style={{ gap: 8 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span
        className="shrink-0"
        style={{
          fontFamily: 'Manrope',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '16px',
          letterSpacing: '0.4px',
          width: 60,
          color: TEXT_DEFAULT,
        }}
      >
        {label}
      </span>
      {/* Segments rendered directly as % of the track (flex-1 container) with
          flexShrink:0 so equal trackPct values produce identical pixel widths
          across rows — avoids the nested-percentage distortion from widthPct%. */}
      <div
        className="flex-1"
        style={{ height: 12, display: 'flex', alignItems: 'center', gap: 4 }}
      >
        {hasEngagement && viewTrackPct > 0 && (
          <div
            style={{
              width: `${viewTrackPct}%`,
              minWidth: 12,
              height: '100%',
              background: ENGAGEMENT_COLORS.view,
              borderRadius: 20,
              flexShrink: 0,
            }}
          />
        )}
        {hasEngagement && likeTrackPct > 0 && (
          <div
            style={{
              width: `${likeTrackPct}%`,
              minWidth: 12,
              height: '100%',
              background: ENGAGEMENT_COLORS.like,
              borderRadius: 20,
              flexShrink: 0,
            }}
          />
        )}
        {hasEngagement && commentTrackPct > 0 && (
          <div
            style={{
              width: `${commentTrackPct}%`,
              minWidth: 12,
              height: '100%',
              background: ENGAGEMENT_COLORS.comments,
              borderRadius: 20,
              flexShrink: 0,
            }}
          />
        )}
      </div>

      {/* Hover tooltip — matches the donut tooltip card (Figma spec). */}
      {hovered && hasEngagement && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 82,
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '8px 18px',
            background: '#FFFFFF',
            borderRadius: 8,
            boxShadow: '0px 2px 6px 2px #00000026',
            fontFamily: 'Manrope, sans-serif',
            whiteSpace: 'nowrap',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 12,
              lineHeight: '16px',
              color: '#020D3A',
            }}
          >
            {label}
          </span>
          <div style={{ display: 'flex', gap: 16 }}>
            <EngagementTooltipMetric
              color={ENGAGEMENT_COLORS.view}
              text={`${formatCount(views)} Views`}
            />
            <EngagementTooltipMetric
              color={ENGAGEMENT_COLORS.like}
              text={`${formatCount(likes)} Likes`}
            />
            <EngagementTooltipMetric
              color={ENGAGEMENT_COLORS.comments}
              text={`${formatCount(comments)} Comments`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const EngagementTooltipMetric = ({
  color,
  text,
}: {
  color: string;
  text: string;
}) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span
      style={{
        width: 16,
        height: 16,
        borderRadius: 10,
        background: color,
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
    <span
      style={{
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '16px',
        color: '#020D3A',
      }}
    >
      {text}
    </span>
  </span>
);

export default ContentAnalysisComponent;
